import { boot } from "@origin-digital/bootloader";
import { configFetch } from "@origin-digital/daxi";
import { initHandler } from "@origin-digital/mesh";
import { initWebChat } from "@origin-digital/origin-chat";
import { initBraze } from "@origin-digital/braze";
import { initGeoTargeting } from "@origin-digital/geo-targeting";
import { EnvironmentNames } from "@origin-digital/platform-enums";
import { isNative as verifyIsNative } from "@origin-digital/platform-helpers";
import { configAnalytics } from "@origin-digital/reporting-client";
import { parseQueryParamsWithNativeGate } from "@origin-digital/native-features";
import { isChatClientSdkEnabled } from "./chatClientAvailability";
import { FEATURE_FLAGS, getDefaultFeatures } from "./features";
import { shouldInitialize } from "./utils";

// Snapshot env once at boot. Freezing prevents post-bootstrap script
// injections (3P scripts, late XSS) from rewriting env to flip security
// gates at call time. Reads through `window.oetal.env` elsewhere will hit
// the frozen object.
if (typeof window !== "undefined" && window.oetal && window.oetal.env) {
  Object.freeze(window.oetal.env);
}

const environment = window.oetal.env.name as EnvironmentNames;

const isSSR = window.oetal.ssr.onServer;
const {
  allowedScopes = [],
  authenticated = false,
  enableChat,
  enableChatClientSdk: routeChatClientSdkEnabled,
  enableSurvicate = true,
} = window.oetal.pageData;

const enableChatClientSdk = isChatClientSdkEnabled({
  globallyEnabled:
    getDefaultFeatures(environment)[FEATURE_FLAGS.CHAT_CLIENT_SDK],
  routeEnabled: routeChatClientSdkEnabled,
});

const disableAuthCheck = ["local", "dev"].includes(environment);
const isNative = verifyIsNative();

let path = "";
let parameters: Record<string, string> = {};
if (typeof window !== "undefined") {
  path = window.location.pathname;
  // Strip the native emulator query param in prod-like envs. This is the
  // single high-level gate: all downstream consumers (mesh, braze, boot)
  // only see parameters cleared by env.
  parameters = parseQueryParamsWithNativeGate(
    window.location.search,
    environment
  );
}

initHandler({
  isNative,
  environment,
  parameters,
  enableChatClientSdk,
});

configFetch({
  environment: environment as any,
  disableAuthCheck,
});

if (enableChat) {
  initWebChat();
}

initGeoTargeting();

initBraze({
  environment,
  path,
  parameters,
});

configAnalytics({
  debugToConsole: true,
});

if (enableSurvicate && shouldInitialize()) {
  import("@origin-digital/survicate").then((res) =>
    res.initSurvicate(environment)
  );
}

boot({
  isNative,
  authenticated,
  isSSR,
  path,
  parameters,
  environment,
  allowedScopes,
});
